import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Prod01 } from '../constants/ActionType';
import { useSortableTable, SortableTh, nameFromString } from './SortableTable';
import { formatRange } from '../utils/dateRange';
import { formatDate, formatTime } from '../utils/dateFormat';

/*
 * CCM time-tracking "Patient summary": minutes completed this ET calendar month vs the next billing threshold,
 * per patient and program, for one nurse (hrId) or all nurses grouped by nurse. Detailed log below (showLog).
 */
export const NEEDS_TIME = ['not_started', 'needs'];
export const PROGRAMS = ['CCM', 'PCM', 'APCM'];

export const SUMMARY_SORT_COLUMNS = {
    patientName: { type: 'name', value: (r) => nameFromString(r?.patientName) },
    program: { type: 'string', value: (r) => r?.program },
    minutesThisMonth: { type: 'number', value: (r) => r?.minutesThisMonth, defaultDir: 'desc' },
    minutesInRange: { type: 'number', value: (r) => r?.minutesInRange, defaultDir: 'desc' },
    nextThreshold: { type: 'number', value: (r) => r?.nextThreshold },
    minutesNeeded: { type: 'number', value: (r) => r?.minutesNeeded, defaultDir: 'desc' },
    status: { type: 'number', value: (r) => r?.statusRank },
    lastActivity: { type: 'date', value: (r) => r?.lastActivity, defaultDir: 'desc' },
    nurseNames: { type: 'string', value: (r) => r?.nurseNames },
};
const LOG_SORT_COLUMNS = {
    when: { type: 'date', value: (r) => (r?.date ? `${r.date} ${r.time || ''}`.trim() : null) },
    patientName: { type: 'name', value: (r) => nameFromString(r?.patientName) },
    program: { type: 'string', value: (r) => r?.program },
    minutes: { type: 'number', value: (r) => r?.minutes },
    staffName: { type: 'string', value: (r) => r?.staffName },
};

export const visibleRows = (rows, { needsOnly = false, program = '' } = {}) =>
    (rows || []).filter((r) => (!program || r.program === program) && (!needsOnly || NEEDS_TIME.includes(r.statusKey)));

export const summaryTotals = (rows) => {
    const list = rows || [];
    const total = (f) => list.reduce((s, r) => s + (Number(f(r)) || 0), 0);
    return {
        patients: new Set(list.map((r) => r.patientId)).size,
        totalMinutes: total((r) => r.minutesThisMonth),
        rangeMinutes: total((r) => r.minutesInRange),
        complete: list.filter((r) => r.statusKey === 'complete').length,
        needingTime: list.filter((r) => NEEDS_TIME.includes(r.statusKey)).length,
        noRequirement: list.filter((r) => r.statusKey === 'no_requirement').length,
        minutesStillNeeded: total((r) => r.minutesNeeded),
    };
};

const STATUS_STYLE = {
    not_started: { background: '#fdecea', color: '#a61b1b' },
    needs: { background: '#fff4e5', color: '#8a4b00' },
    complete: { background: '#e7f6ec', color: '#1e6b35' },
    no_requirement: { background: '#eef1f5', color: '#44516a' },
};
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
const ymdLabel = (ymd) => formatDate(ymd, '—');

export const TotalsLine = ({ totals, showRange }) => (
    <>
        <td><strong>Totals: {plural(totals.patients, 'patient')}</strong></td>
        <td />
        <td><strong>{totals.totalMinutes}</strong></td>
        {showRange && <td><strong>{totals.rangeMinutes}</strong></td>}
        <td />
        <td><strong>{totals.minutesStillNeeded}</strong></td>
        <td colSpan={3}>
            <strong>{totals.complete} complete · {totals.needingTime} need time</strong>
            {totals.noRequirement ? ` · ${totals.noRequirement} no time requirement` : ''}
        </td>
    </>
);

const GroupTable = ({ group, showRange, filters }) => {
    const rows = useMemo(() => visibleRows(group.rows, filters), [group.rows, filters]);
    const sort = useSortableTable(rows, { id: 'ccmTimeSummary.patients', columns: SUMMARY_SORT_COLUMNS, defaultSort: { key: 'patientName', dir: 'asc' } });
    const totals = summaryTotals(rows);
    const th = (key, label, title) => <SortableTh key={key} scope="col" sortKey={key} sort={sort} title={title}>{label}</SortableTh>;
    return (
        <div className="table-responsive">
            <table className="table table-sm table-bordered table-hover ccm-summary-table" data-testid="ccm-summary-table">
                <thead className="thead-light">
                    <tr>
                        {th('patientName', 'Patient')}
                        {th('program', 'Program')}
                        {th('minutesThisMonth', 'Minutes this month')}
                        {showRange && th('minutesInRange', 'Minutes in range')}
                        {th('nextThreshold', 'Next threshold')}
                        {th('minutesNeeded', 'Still needed')}
                        {th('status', 'Status')}
                        {th('lastActivity', 'Last activity')}
                        {th('nurseNames', 'Nurse(s)')}
                    </tr>
                </thead>
                <tbody>
                    {sort.rows.length === 0 && (
                        <tr><td colSpan={showRange ? 9 : 8} className="text-center text-muted">No patients match.</td></tr>
                    )}
                    {sort.rows.map((r) => (
                        <tr key={r.key} data-testid="ccm-summary-row">
                            <td style={{ whiteSpace: 'nowrap' }}>{r.patientName}</td>
                            <td>{r.program}{r.complex ? ' (complex)' : ''}</td>
                            <td>{r.minutesThisMonth}{r.providerMinutesThisMonth ? <small className="text-muted"> (+{r.providerMinutesThisMonth} provider)</small> : null}</td>
                            {showRange && <td>{r.minutesInRange}</td>}
                            <td>{r.nextThreshold == null ? 'None' : `${r.nextThreshold} min`}</td>
                            <td>{r.minutesNeeded}</td>
                            <td><span className="badge" style={{ ...STATUS_STYLE[r.statusKey], fontSize: 12, fontWeight: 600 }}>{r.status}</span></td>
                            <td>{ymdLabel(r.lastActivity)}</td>
                            <td>{r.nurseNames || '—'}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="table-active" data-testid="ccm-summary-totals"><TotalsLine totals={totals} showRange={showRange} /></tr>
                </tfoot>
            </table>
        </div>
    );
};

const LogTable = ({ log }) => {
    const sort = useSortableTable(log, { id: 'ccmTimeSummary.log', columns: LOG_SORT_COLUMNS, defaultSort: { key: 'when', dir: 'desc' } });
    const th = (key, label) => <SortableTh key={key} scope="col" sortKey={key} sort={sort}>{label}</SortableTh>;
    return (
        <div className="table-responsive">
            <table className="table table-sm table-striped" data-testid="ccm-log-table">
                <thead>
                    <tr>{th('when', 'Date / time')}{th('patientName', 'Patient')}{th('program', 'Program')}{th('minutes', 'Minutes')}{th('staffName', 'Staff')}<th scope="col">Activity</th><th scope="col">Note</th></tr>
                </thead>
                <tbody>
                    {sort.rows.length === 0 && <tr><td colSpan={7} className="text-center text-muted">No time entries in this range.</td></tr>}
                    {sort.rows.map((l) => (
                        <tr key={l.id}>
                            <td>{ymdLabel(l.date)} {formatTime(l.time)}</td>
                            <td>{l.patientName}</td>
                            <td>{l.program}</td>
                            <td>{l.minutes}</td>
                            <td>{l.staffName}{l.provider ? ' (provider)' : ''}</td>
                            <td>{l.activityType || (l.interactive ? 'Interactive' : '')}</td>
                            <td style={{ maxWidth: 360, whiteSpace: 'pre-wrap' }}>{l.note}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const CcmTimeSummary = ({ hrId, startDate, endDate, showLog = true }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [needsOnly, setNeedsOnly] = useState(false);
    const [program, setProgram] = useState('');
    const [exporting, setExporting] = useState('');
    const params = useMemo(() => ({ startDate, endDate, ...(hrId ? { hrId } : {}) }), [hrId, startDate, endDate]);
    const filters = useMemo(() => ({ needsOnly, program }), [needsOnly, program]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data: d } = await axios.get(`${Prod01}/ccm-time-summary`, { params });
            setData(d);
        } catch (e) {
            setData(null);
            setError(e?.response?.status === 403 ? 'You do not have access to this report.' : e?.response?.data?.message || 'Could not load the CCM patient summary');
        } finally {
            setLoading(false);
        }
    }, [params]);

    useEffect(() => { load(); }, [load]);

    const exportFile = async (format) => {
        setExporting(format);
        try {
            const res = await axios.get(`${Prod01}/ccm-time-summary/export.${format}`, {
                params: { ...params, ...(needsOnly ? { needsOnly: 'true' } : {}), ...(program ? { program } : {}) },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'text/csv' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `ccm-time-summary-${hrId ? 'nurse' : 'all-nurses'}-${startDate}-to-${endDate}.${format}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setError('Export failed');
        } finally {
            setExporting('');
        }
    };

    const showRange = data ? !data.period.isMonthView : false;
    const allRows = useMemo(() => {
        if (!data) return [];
        const seen = new Map();
        data.groups.forEach((g) => g.rows.forEach((r) => seen.set(r.key, r)));
        return [...seen.values()];
    }, [data]);
    const grand = summaryTotals(visibleRows(allRows, filters));
    const monthLabel = data ? `${data.period.monthLabel}${data.period.monthToDate ? ', to date' : ''}` : '';

    return (
        <section className="ccm-time-summary mt-3" aria-label="CCM patient summary">
            <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
                <h5 className="mb-0" style={{ color: '#004aad' }}>
                    Patient summary{data && data.nurse ? ` — ${data.nurse.name}` : data ? ' — all nurses' : ''}
                </h5>
                <div>
                    <button type="button" className="edit-pill mr-2" onClick={() => exportFile('pdf')} disabled={!data || !!exporting}>{exporting === 'pdf' ? 'Exporting…' : 'Export PDF'}</button>
                    <button type="button" className="edit-pill" onClick={() => exportFile('csv')} disabled={!data || !!exporting}>{exporting === 'csv' ? 'Exporting…' : 'Export CSV (Excel)'}</button>
                </div>
            </div>
            {data && (
                <p className="text-muted mb-2" data-testid="ccm-summary-period">
                    Minutes this month = ET calendar month <strong>{monthLabel}</strong>
                    {showRange ? <> · Range minutes: <strong>{formatRange(data.period.startDate, data.period.endDate)}</strong></> : null} (Eastern Time)
                </p>
            )}
            <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: 16 }}>
                <label className="mb-0">
                    <input type="checkbox" checked={needsOnly} onChange={(e) => setNeedsOnly(e.target.checked)} /> Needs time only
                </label>
                <label className="mb-0">
                    Program{' '}
                    <select aria-label="Program filter" value={program} onChange={(e) => setProgram(e.target.value)}>
                        <option value="">All</option>
                        {PROGRAMS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </label>
            </div>
            {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
            {loading && !data && <p className="text-muted">Loading patient summary…</p>}
            {data && data.groups.length === 0 && <p className="text-muted">No CCM, PCM or APCM patients for this selection.</p>}
            {data && data.groups.map((g) => (
                <div key={g.nurseId || 'unassigned'} className="mb-3" data-testid="ccm-summary-group">
                    {data.mode === 'allNurses' && <h6 className="mt-3 mb-1"><strong>Nurse: {g.nurseName}</strong></h6>}
                    <GroupTable group={g} showRange={showRange} filters={filters} />
                </div>
            ))}
            {data && data.mode === 'allNurses' && data.groups.length > 0 && (
                <table className="table table-sm table-bordered" data-testid="ccm-summary-grand-totals">
                    <tbody><tr className="table-active"><td colSpan={1}><strong>All nurses</strong></td><TotalsLine totals={grand} showRange={showRange} /></tr></tbody>
                </table>
            )}
            {data && (
                <details open className="mb-3" data-testid="ccm-summary-legend">
                    <summary><strong>Time required (legend)</strong></summary>
                    <ul className="mb-0 mt-1" style={{ fontSize: 13 }}>
                        {data.legend.map((l) => <li key={l}>{l}</li>)}
                    </ul>
                </details>
            )}
            {showLog && data && (
                <>
                    <h6 className="mt-3"><strong>Time-entry log</strong> <small className="text-muted">({formatRange(data.period.startDate, data.period.endDate)})</small></h6>
                    <LogTable log={data.log} />
                </>
            )}
        </section>
    );
};

export default CcmTimeSummary;
